/**
 * Group "shop": the meta-game trade of FWP - the shop, the arsenal, Papa John, darts (roulette) and stealing.
 *
 *   shop.buy            buy an item in the shop ('buy_gold' / 'buy_diam' on 'shop', new_interf 5870-5970)
 *   papa.enter          open Papa John's shop for today ('papa_shop', new_interf 5335-5345)      -> ./shop-papa.ts
 *   papa.buy            buy from Papa's assortment of the day (new_interf 5972-6020)              -> ./shop-papa.ts
 *   papa.ask            ask Papa when he comes tomorrow ('papa_ask', new_interf 5324-5334)        -> ./shop-papa.ts
 *   arsenal.sell        sell an implant or an object, or the collection of all nine books         -> ./shop-arsenal.ts
 *   arsenal.sellWeapon  sell one weapon copy ('finish_sell', new_interf 5083-5114)                -> ./shop-arsenal.ts
 *   arsenal.upgrade     raise the rank of one weapon copy ('finish_upgrade', 5133-5173)           -> ./shop-arsenal.ts
 *   arsenal.stimToFight take a stimulant into the next battle ('arsenal_use' on help, 5194-5204)  -> ./shop-arsenal.ts
 *   arsenal.equip       wear a flag, a grave or an implant ('arsenal_use', 5205-5213)             -> ./shop-arsenal.ts
 *   arsenal.readBook    learn the recipes of a book ('arsenal_use' on objs, 5176-5192)            -> ./shop-arsenal.ts
 *   arsenal.clearQuickSlot  empty a quick-weapon slot ('wpclear_<key>', 5490-5494)                -> ./shop-arsenal.ts
 *   roulette.bet        a darts bet ('bet_<currency>_<amount>', new_interf 6164-6219)              -> ./shop-chance.ts
 *   steal.attempt       try to steal a weapon in the shop ('try_steal' -> steal(), 7575-7607)     -> ./shop-chance.ts
 *   steal.apology       talk the shopkeeper out of the penalty ('steal_luck' / 'steal_merch')     -> ./shop-chance.ts
 *
 * Preconditions are the conditions under which new_interf.render shows the control (listed in each op) plus the
 * ownership the handlers never checked; the mutations are the handlers' own, ported line by line. Reason codes
 * besides RULE_REASONS: SHOP_REASONS (./shop-common.ts).
 *
 * Server state: steal.attempt and steal.apology keep the apology dialog of a failed steal as server state
 * (PendingStealApology under serverState.stealApology, ./shop-chance.ts). The backend attaches the player's server
 * state to the context with withServerState(ctx, serverState) (./shop-common.ts) and stores `result.serverState`;
 * every other op of the group ignores server state.
 */
import { argsObject, knownKey, stringArg } from '../args';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import { arsenalOps } from './shop-arsenal';
import { chanceOps } from './shop-chance';
import {
  addWeaponCopy,
  CATEGORY_TABLES,
  checkBuyButtons,
  countBuy,
  CURRENCIES,
  idArg,
  merchantMultiplier,
  newCopy,
  pay,
  SHOP_CATEGORIES,
  shopLists,
  weaponOptimisation,
  type Currency,
  type ShopCategory,
} from './shop-common';
import { papaOps } from './shop-papa';

export interface ShopBuyArgs {
  category: ShopCategory;
  /** DEF key of the item (the shop control 'weapon_<id>'). */
  id: string;
  /** 'gold' = the buy_gold button, 'diam' = buy_diam. */
  currency: Currency;
}

export interface ShopBuyResult {
  category: ShopCategory;
  id: string;
  currency: Currency;
  /** Amount of `currency` paid. */
  price: number;
  /** Copies / pieces owned after the purchase. */
  count: number;
  /** The first copy of a weapon (the game shows it as FWP.new_weapon, execute_after 'post new weapon'). */
  newWeapon: boolean;
}

const buy = {
  /**
   * shop.buy { category, id, currency }.
   * Preconditions: the shop lets the player in (today_steals < 8, init('shop') 673-684), the item is listed
   * (shopLists), and the chosen buy button is visible (checkBuyButtons with the Merchant multiplier; implants never get
   * the discount, new_interf 2195-2197 / 5875-5877).
   * Mutations (new_interf 5870-5969): the item (+ weapon_optimisation for weapons), auto-equip of flags / graves /
   * implants, tasks 7->8 (flag), 29->30 (implant), 2->3 (stimulant), the price, and for weapons achiev.buys (class > 1)
   * and tasks 4->5 (class >= 2), 10->11 (>= 3), 16->17 (>= 4), 21->22 (energy weapon 'h').
   */
  'shop.buy': defineOp<ShopBuyArgs, ShopBuyResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['category', 'id', 'currency']);
      return { category: stringArg(o, 'category', { oneOf: SHOP_CATEGORIES }), id: idArg(o), currency: stringArg(o, 'currency', { oneOf: CURRENCIES }) };
    },
    run(state, { category, id, currency }, ctx) {
      const p = state.player;
      if (Number(p.today_steals) >= 8) throw new RuleError('shop_closed', { todaySteals: p.today_steals });
      const { def: defName, store: storeName } = CATEGORY_TABLES[category];
      const table: Record<string, any> = ctx.defs[defName];
      knownKey(table, id);
      const def = table[id];
      if (!shopLists(category, id, def)) throw new RuleError('not_available', { id, reason: 'not_listed' });
      let k = merchantMultiplier(p);
      if (category == 'impl') k = 1;
      const { price } = checkBuyButtons(p, ctx.defs, category, id, def, currency, k);

      const store: Record<string, any> = p[storeName];
      if (category == 'weapons') {
        addWeaponCopy(store, id, newCopy());
        weaponOptimisation(store);
      } else if (!store[id]) {
        store[id] = 1;
      } else {
        ++store[id];
      }
      if (category == 'flags') {
        p.cur_flag = def.id;
        if (p.cur_task == 7) p.cur_task = 8;
      }
      if (category == 'rip') p.cur_rip = def.id;
      if (category == 'impl') {
        p.cur_impl = def.id;
        if (p.cur_task == 29) p.cur_task = 30;
      }
      if (category == 'help' && p.cur_task == 2) p.cur_task = 3;
      pay(p, currency, price);
      let newWeapon = false;
      if (category == 'weapons') {
        if (def.wpclass > 1) countBuy(p);
        if (def.wpclass >= 2 && p.cur_task == 4) p.cur_task = 5;
        if (def.wpclass >= 3 && p.cur_task == 10) p.cur_task = 11;
        if (def.wpclass >= 4 && p.cur_task == 16) p.cur_task = 17;
        if (def.wptype == 'h' && p.cur_task == 21) p.cur_task = 22;
        newWeapon = store[id][0] == 1;
      }
      const count = category == 'weapons' ? store[id][0] : store[id];
      return { state, result: { category, id, currency, price, count, newWeapon } };
    },
  }),
};

export const shopOps = {
  ...buy,
  ...papaOps,
  ...arsenalOps,
  ...chanceOps,
};

export { SHOP_REASONS, type Currency, type ShopCategory } from './shop-common';
